// src/components/forms/base/BaseEmailCaptureForm.tsx

'use client'

import { forwardRef } from 'react'
import { cn } from '~/lib/utils'
import { useEmailForm } from '~/components/forms/base/useEmailForm'
import type { BaseFormProps, RenderProps } from '~/components/forms/base/types'

export const BaseEmailCaptureForm = forwardRef<HTMLFormElement, BaseFormProps>(({
  // Core Props
  id,
  name,
  className,
  style,
  
  // Configurations
  template,
  crm,
  validation,
  theme,
  content,
  features,
  analytics,
  
  // Event Handlers
  onSubmit,
  onError,
  onSuccess,
  onChange,
  onValidation,
  
  // Accessibility
  aria,
  
  // Children
  children
}, ref) => {
  const form = useEmailForm({
    validation,
    crm,
    template,
    analytics,
    onSubmit,
    onError,
    onSuccess,
    onChange,
    onValidation
  })

  const {
    email,
    error,
    isSubmitting,
    isSuccess,
    isValid,
    isDirty,
    isTouched,
    handleChange,
    handleSubmit,
    handleBlur,
    handleReset,
    validate
  } = form

  const formState = {
    email,
    error,
    isSubmitting,
    isSuccess,
    isValid,
    isDirty,
    isTouched
  }

  const formHandlers = {
    handleChange,
    handleSubmit,
    handleBlur,
    handleReset,
    validate
  }

  const formConfig = {
    theme,
    content,
    features
  }

  return (
    <form
      ref={ref}
      id={id}
      name={name}
      className={cn(
        'relative w-full',
        theme?.className?.form,
        className
      )}
      style={{
        ...theme?.style?.form,
        ...style
      }}
      onSubmit={handleSubmit}
      aria-label={aria?.label}
      aria-describedby={aria?.describedBy}
    >
      {typeof children === 'function' 
        ? (children as (props: RenderProps) => React.ReactNode)({ 
            state: formState, 
            handlers: formHandlers, 
            config: formConfig 
          })
        : children}
    </form>
  )
})

BaseEmailCaptureForm.displayName = 'BaseEmailCaptureForm' 